import Vue from 'vue';
import { mapGetters } from 'vuex';
import InlineText from '@editor/droplets/_bubble-options/components/inline-text';
import _ from 'lodash';
import { cssGenerator } from '@/mixins/css-generator';
import tooltip from '@/mixins/components/tooltip';
import { SQUASH_DROPLET } from '@/constants';
import DropletOptions from '../_bubble-options';
import TableOptions from './components/_edit-options';
import schema from './droplet.schema';
const dropletSchema = schema();

export default Vue.component('responsive-table', {
  template: require('./template.html'),
  mixins: [cssGenerator],
  computed: {
    ...mapGetters([
      'currentPage',
      'viewport',
      'localStyle',
      'activeDroplet',
      'activeState',
      'animationPreview',
      'dropletObserver',
      'dragging',
      'globalBubbleOptionsOpen',
    ]),
    profile() {
      return this._dropletType(this.droplet);
    },
    hideTransform() {
      return this.dragging || this.globalBubbleOptionsOpen || this.openToolBar;
    },
    roamingTemplate() {
      return this.currentPage.type !== 'template' && this.droplet.templateRegion;
    },
    isActiveDroplet() {
      return this.activeDroplet._id === this.droplet._id;
    },
    displayStatus() {
      let settings = {
        desktop: 'default',
        tablet: 'default',
        mobile: 'default',
      };
      const selector = _.find(this.droplet.mediaQueries, { selector: ', .drzTable' });
      if (selector) {
        const property = _.find(selector.properties, { property: 'display' });
        if (property) {
          settings = property;
        }
      }
      return settings;
    },
  },
  data() {
    return {
      type: dropletSchema.type,
      SQUASH_DROPLET,
      display: {
        top: null,
        left: null,
        right: null,
        bottom: null,
        ctPaddingTop: null,
        ctPaddingBottom: null,
        ctPaddingLeft: null,
        ctPaddingRight: null,
        transform: null,
        perspective: null,
        'box-shadow': null,
      },
      hidden: false,
      dropletHovered: false,
      setBubbleSize: 'dropletBubble-wide',
      showEditBubble: false,
      openToolBar: false,
      showRevealClass: false,
      toolbar: [
        {
          type: 'bold',
          tip: 'Bold',
        },
        {
          type: 'italic',
          tip: 'Italic',
        },
        {
          type: 'underline',
          tip: 'Underline',
        },
        {
          type: 'fontSize',
          tip: 'Font Size',
          sizes: ['8', '9', '10', '11', '12', '14', '16', '24', '36'],
        },
        {
          type: 'fontName',
          tip: 'Font',
          fonts: [
            'Arial',
            'Arial Black',
            'Comic Sans MS',
            'Courier New',
            'Impact',
            'Lucida Console',
            'Source Sans Pro',
            'Times New Roman',
            'Verdana',
          ],
        },
        {
          type: 'foreColor',
          tip: 'Font Color',
          colors: [
            '#ffffff',
            '#cccccc',
            '#808080',
            '#343434',
            '#000000',
            '#C8E6C9',
            '#81C784',
            '#4CAF50',
            '#388E3C',
            '#1B5E20',
            '#B2EBF2',
            '#4DD0E1',
            '#00BCD4',
            '#0097A7',
            '#006064',
            '#BBDEFB',
            '#64B5F6',
            '#2196F3',
            '#1976D2',
            '#0D47A1',
            '#D1C4E9',
            '#9575CD',
            '#673AB7',
            '#512DA8',
            '#311B92',
            '#F8BBD0',
            '#F06292',
            '#E91E63',
            '#C2185B',
            '#880E4F',
            '#FFCDD2',
            '#E57373',
            '#F44336',
            '#D32F2F',
            '#B71C1C',
            '#FFE0B2',
            '#FFB74D',
            '#FF9800',
            '#F57C00',
            '#E65100',
            '#FFF9C4',
            '#FFF176',
            '#FFEB3B',
            '#FBC02D',
            '#F57F17',
          ],
        },
      ],
    };
  },
  name: 'responsive-table',
  props: ['droplet', 'items', 'index'],
  components: {
    DropletOptions,
    TableOptions,
    InlineText,
    tooltip,
  },
  watch: {
    isActiveDroplet(is) {
      if (is) {
        this._scrollToDroplet(this.$el);
      }
    },
    'activeDroplet._id': function c(id) {
      if (!id) {
        this.showRevealClass = false;
      }
    },
    activeState(state) {
      if (state === ':reveal' && this.isActiveDroplet) {
        this.showRevealClass = true;
      } else {
        this.showRevealClass = false;
      }
    },
    animationPreview(should) {
      if (should && this.isActiveDroplet) {
        const droplet = this.$refs.dropletRow;
        const $droplet = $(droplet);
        $droplet.drzOnReveal({
          editing: this.isActiveDroplet,
          child: '.drzTable',
        });
      }
    },
    viewport() {
      this.callCssGenerator();
      this.observeVisibility();
    },
    'localStyle.value': function setLocals() {
      this.setDisplay();
    },
    dropletObserver(id) {
      if (this.droplet._id === id) {
        this.observeVisibility();
        this.$store.dispatch('setDropletObserver', false);
      }
    },
  },
  mounted() {
    this.callCssGenerator();
    $(document).ready(this.bindPlugin);
    this.observeVisibility();
  },
  methods: {
    callCssGenerator() {
      return this.generateCSS({
        drp: this.droplet,
      });
    },
    observeVisibility() {
      const display = this.displayStatus;
      if (display[this.viewport] === 'none' || (display[this.viewport] === 'default' && display.desktop === 'none')) {
        this.hidden = true;
      } else {
        this.hidden = false;
      }
    },
    handleToolbar(v) {
      this.openToolBar = v;
      this.showEditBubble = false;
    },
    async updateHeader(text, head) {
      const payload = {};
      payload.head = head;
      payload.text = text;
      payload.text = text.trim() === '' ? false : text;
      if (head.text !== text) {
        await this.$store.dispatch('setTableHeaderText', payload);
        this.bouncePlugin();
      }
    },
    updateTd(text, td) {
      const payload = {};
      payload.td = td;
      payload.text = text;
      payload.text = text.trim() === '' ? false : text;
      if (td.text !== text) {
        this.$store.dispatch('setTableTdText', payload);
      }
    },
    setDisplay() {
      if (this.activeDroplet._id === this.droplet._id) {
        const styles = this.localStyle;
        if (styles.properties.length > 0) {
          _.forEach(styles.properties, (property) => {
            this.$set(this.display, property, styles.value);
          });
        } else {
          _.forOwn(this.display, (value, key) => {
            this.display[key] = null;
          });
        }
      }
    },
    bindPlugin() {
      const $table = $(this.$refs.table);
      $table.responsiveTables();
    },
    unBindPlugin() {
      const $table = $(this.$refs.table);
      $table.responsiveTables.destroy($table);
    },
    bouncePlugin() {
      this.unBindPlugin();
      this.bindPlugin();
    },
    promise(action, error) {
      const promise = new Promise((resolve, reject) => {
        if (action) {
          resolve();
        } else {
          reject(Error(`Error in Tables: ${error}`));
        }
      });
      promise.then(() => {
        this.bouncePlugin();
      });
    },
    changeBubbleSize(size) {
      this.setBubbleSize = `dropletBubble-${size}`;
    },
    toggleBubble() {
      if (!this.roamingTemplate && !this.openToolBar) {
        this.showEditBubble = !this.showEditBubble;
      }
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/droplets/table/index.js